import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import DashboardBranchFilter, {
  useDashboardBranch,
} from "../Includes/DashboardBranchFilter";
import "./FitnessManagerDashboard.css";

const BLUE = "#2563eb";
const PURPLE = "#7c3aed";

const money = (v) =>
  `Rs ${Number(v || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-PK");

const Kpi = ({ label, value, sub, tone, icon }) => (
  <div className={`fm-kpi fm-tone-${tone}`}>
    <div className="fm-kpi-body">
      <p className="fm-kpi-label">{label}</p>
      <h4 className="fm-kpi-value">{value}</h4>
      {sub ? <p className="fm-kpi-sub">{sub}</p> : null}
    </div>
    <div className="fm-kpi-icon">
      <i className={`bi ${icon}`}></i>
    </div>
  </div>
);

const Metric = ({ label, value }) => (
  <div className="fm-metric">
    <span className="fm-metric-label">{label}</span>
    <strong className="fm-metric-value">{value}</strong>
  </div>
);

const typePill = (type) => {
  const t = String(type || "").toLowerCase();
  if (t === "pt") return "fm-pill-pt";
  if (t === "gx") return "fm-pill-gx";
  if (t === "befit") return "fm-pill-befit";
  if (t === "spt") return "fm-pill-spt";
  return "";
};

export default function FitnessManagerDashboard() {
  const { state } = useContext(NoteState);
  const { first_name, last_name, branch_id, role } = state;
  const { branch, setBranch } = useDashboardBranch(role, branch_id);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await UserServices.fitnessManagerDashboardSummary(branch, {
        timeout: 25000,
        date,
      });
      const payload =
        res?.data?.data && typeof res.data.data === "object" ? res.data.data : res?.data;
      if (res.status === 200 && payload) {
        setData(payload);
      } else {
        setData(null);
        setError("No dashboard data for this branch.");
      }
    } catch (err) {
      console.error(err);
      setData(null);
      setError(
        err.code === "ECONNABORTED"
          ? "Dashboard is taking too long. Try again."
          : "Could not load Fitness Manager Dashboard."
      );
    } finally {
      setLoading(false);
    }
  }, [branch, date]);

  useEffect(() => {
    load();
  }, [load]);

  const trend = data?.trend || [];
  const sessions = data?.sessions || {};
  const sales = data?.sales || {};
  const trainers = data?.trainers || {};
  const footfall = data?.footfall || {};
  const presentTrainers = data?.present_trainers || [];
  const recent = data?.recent_sessions || [];
  const trainerWiseSales = data?.trainer_wise_sales || [];
  const gtReferrals = data?.gt_referrals || { rows: [], totals: {} };
  const gtRows = gtReferrals.rows || [];
  const gtDetails = gtReferrals.details || [];
  const gtTotals = gtReferrals.totals || {};
  const monthLabel = data?.month_label || moment(date).format("MMM YYYY");

  const trainerSalesTotals = useMemo(() => {
    return trainerWiseSales.reduce(
      (acc, row) => {
        acc.sales += Number(row.sales || 0);
        acc.clients += Number(row.clients || 0);
        acc.net += Number(row.net_price || 0);
        return acc;
      },
      { sales: 0, clients: 0, net: 0 }
    );
  }, [trainerWiseSales]);

  const branchLabel = data?.branch_label || "Branch";

  const quickLinks = useMemo(
    () => [
      { to: "/new-pt-bookings", icon: "bi-calendar2-check", label: "PT Bookings" },
      { to: "/new-pt-packages", icon: "bi-person-plus", label: "New PT Clients" },
      { to: "/trainer-appointments", icon: "bi-clock-history", label: "PT Appointments" },
      { to: "/pt-sales-report", icon: "bi-cash-stack", label: "PT Sales" },
      { to: "/new-gx-bookings", icon: "bi-music-note-list", label: "GX Bookings" },
      { to: "/gx-classes-attendance", icon: "bi-clipboard-check", label: "GX Attendance" },
      { to: "/new-befit-bookings", icon: "bi-heart-pulse", label: "Befit" },
      { to: "/new-spt-bookings", icon: "bi-people", label: "SPT" },
      { to: "/fitness-plan", icon: "bi-journal-text", label: "Fitness Plans" },
      { to: "/hr-session-portal", icon: "bi-ui-checks-grid", label: "Session Portal" },
      { to: "/time-slot", icon: "bi-grid-3x3-gap", label: "Time Slots" },
      { to: "/announcements", icon: "bi-megaphone", label: "Announcements" },
    ],
    []
  );

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design fm-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="fm-topbar">
              <div>
                <p className="fm-eyebrow">Fitness Manager</p>
                <h2 className="fm-title">
                  <i className="bi bi-activity me-2"></i>
                  Fitness Dashboard
                </h2>
                <p className="fm-subtitle">
                  {moment(data?.date || date).format("dddd, DD MMM YYYY")}
                  {first_name
                    ? ` · Welcome ${first_name}${last_name ? ` ${last_name}` : ""}`
                    : ""}
                  {` · ${branchLabel}`}
                </p>
              </div>
              <div className="fm-toolbar">
                <DashboardBranchFilter
                  role={role}
                  loginBranchId={branch_id}
                  value={branch}
                  onChange={setBranch}
                />
                <input
                  type="date"
                  className="form-control"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  aria-label="Date"
                />
                <button type="button" className="btn btn-outline-danger" onClick={load} disabled={loading}>
                  {loading ? "Loading…" : "Refresh"}
                </button>
              </div>
            </div>

            {error ? <div className="alert alert-warning py-2">{error}</div> : null}

            {loading && !data ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status" />
                <div className="text-muted mt-2 small">Loading fitness dashboard…</div>
              </div>
            ) : (
              <>
                <h3 className="fm-section-title">Today at a glance</h3>
                <div className="fm-kpi-grid">
                  <Kpi
                    label="Floor Footfall"
                    value={num(footfall.total)}
                    sub={`Male ${num(footfall.males)} · Female ${num(footfall.females)}`}
                    icon="bi-people"
                    tone="teal"
                  />
                  <Kpi
                    label="PT Trainers Present"
                    value={num(trainers.present)}
                    sub={`of ${num(trainers.total)} · Absent ${num(trainers.absent)} · Late ${num(trainers.late)}`}
                    icon="bi-person-badge"
                    tone="blue"
                  />
                  <Kpi
                    label="PT Sessions"
                    value={num(sessions.pt_total)}
                    sub={`Delivered ${num(sessions.pt_delivered)}`}
                    icon="bi-lightning-charge"
                    tone="purple"
                  />
                  <Kpi
                    label="GX Sessions"
                    value={num(sessions.gx_total)}
                    sub={`Delivered ${num(sessions.gx_delivered)} · Befit ${num(sessions.befit_total)} · SPT ${num(sessions.spt_total)}`}
                    icon="bi-music-note-beamed"
                    tone="amber"
                  />
                  <Kpi
                    label="PT Sales Today"
                    value={money(sales.pt_today?.net)}
                    sub={`Qty ${num(sales.pt_today?.qty)} · New ${num(sales.pt_new_today?.qty)} · Renew ${num(sales.pt_renew_today?.qty)}`}
                    icon="bi-cash-coin"
                    tone="red"
                  />
                  <Kpi
                    label="Active PT Clients"
                    value={num(data?.active_pt_clients)}
                    sub={`GX sales today ${money(sales.gx_today?.net)} · PT MTD ${money(sales.pt_mtd?.net)}`}
                    icon="bi-person-check"
                    tone="green"
                  />
                </div>

                <h3 className="fm-section-title">General Trainers · {monthLabel}</h3>
                <div className="fm-panel mb-4">
                  <div className="fm-panel-head">
                    <h5>Trainer-wise BeFit performance</h5>
                    <span className="fm-chip">
                      {num(gtTotals.trainers)} trainers · {num(gtTotals.present)} present ·{" "}
                      {num(gtTotals.total)} referrals · {num(gtTotals.converted)} converted ·{" "}
                      {num(gtTotals.pending)} pending · {num(gtTotals.not_converted)} not converted
                    </span>
                  </div>
                  <div className="fm-table-wrap">
                    <table className="fm-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          {data?.is_all_branches ? <th>Branch</th> : null}
                          <th>General Trainer</th>
                          <th>Today</th>
                          <th>Assessments</th>
                          <th>BeFit given</th>
                          <th>Converted to PT</th>
                          <th>Pending</th>
                          <th>Not converted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gtRows.length ? (
                          gtRows.map((row, idx) => (
                            <tr key={row.trainer_id}>
                              <td>{idx + 1}</td>
                              {data?.is_all_branches ? <td>{row.branch}</td> : null}
                              <td>{row.name}</td>
                              <td>{row.present ? row.checkin : "Absent"}</td>
                              <td>{num(row.assessments)}</td>
                              <td>{num(row.total)}</td>
                              <td>{num(row.converted)}</td>
                              <td>{num(row.pending)}</td>
                              <td>{num(row.not_converted)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={data?.is_all_branches ? 9 : 8} className="text-muted">
                              No General Trainers for this branch.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="fm-panel mb-4">
                  <div className="fm-panel-head">
                    <h5>BeFit referral details</h5>
                    <span className="fm-chip">{num(gtDetails.length)} in {monthLabel}</span>
                  </div>
                  <div className="fm-table-wrap">
                    <table className="fm-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>General Trainer</th>
                          <th>Client</th>
                          <th>Referred PT</th>
                          <th>Conversion</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gtDetails.length ? (
                          gtDetails.map((row) => (
                            <tr key={row.id}>
                              <td>{row.referral_date ? moment(row.referral_date).format("DD MMM YYYY") : "—"}</td>
                              <td>{row.gt_name}</td>
                              <td>
                                {row.client_id ? (
                                  <Link to={`/client-home/${row.client_id}`}>{row.client_name}</Link>
                                ) : row.client_name}
                              </td>
                              <td>{row.pt_name}</td>
                              <td>
                                {row.converted_to_pt}
                                {row.converted_at ? ` · ${moment(row.converted_at).format("DD MMM")}` : ""}
                              </td>
                              <td>{row.notes || "—"}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-muted">
                              No BeFit referrals from General Trainers in {monthLabel}.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <h3 className="fm-section-title">Sessions & floor</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-7">
                    <div className="fm-panel">
                      <div className="fm-panel-head">
                        <h5>PT vs GX sessions (7 days)</h5>
                        <span className="fm-chip">Real attendance</span>
                      </div>
                      <div className="fm-chart">
                        <ResponsiveContainer width="100%" height={280}>
                          <LineChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={40} allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="pt" name="PT" stroke={BLUE} strokeWidth={2.5} dot={false} />
                            <Line type="monotone" dataKey="gx" name="GX" stroke={PURPLE} strokeWidth={2.5} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-xl-5">
                    <div className="fm-panel h-100">
                      <div className="fm-panel-head">
                        <h5>Sales snapshot</h5>
                      </div>
                      <Metric label="PT today (net)" value={money(sales.pt_today?.net)} />
                      <Metric label="PT new / renew qty" value={`${num(sales.pt_new_today?.qty)} / ${num(sales.pt_renew_today?.qty)}`} />
                      <Metric label="PT month-to-date" value={money(sales.pt_mtd?.net)} />
                      <Metric label="GX today (net)" value={money(sales.gx_today?.net)} />
                      <Metric label="GX month-to-date" value={money(sales.gx_mtd?.net)} />
                      <Metric label="Active PT packages" value={num(data?.active_pt_clients)} />
                    </div>
                  </div>
                </div>

                <div className="fm-panel mb-4">
                  <div className="fm-panel-head">
                    <h5>Trainer-wise PT sales ({monthLabel})</h5>
                    <span className="fm-chip">
                      {num(trainerWiseSales.length)} trainers · Net {money(trainerSalesTotals.net)}
                    </span>
                  </div>
                  <div className="fm-table-wrap">
                    <table className="fm-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Trainer</th>
                          <th>Sales</th>
                          <th>Clients</th>
                          <th>Total Price</th>
                          <th>Discount</th>
                          <th>Tax</th>
                          <th>Net Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trainerWiseSales.length ? (
                          <>
                            {trainerWiseSales.map((row, idx) => (
                              <tr key={row.trainer_id || row.trainer}>
                                <td>{idx + 1}</td>
                                <td>{row.trainer}</td>
                                <td>{num(row.sales)}</td>
                                <td>{num(row.clients)}</td>
                                <td>{money(row.total_price)}</td>
                                <td>{money(row.discount)}</td>
                                <td>{money(row.tax)}</td>
                                <td>
                                  <strong>{money(row.net_price)}</strong>
                                </td>
                              </tr>
                            ))}
                            <tr className="fm-total-row">
                              <td colSpan={2}>Total</td>
                              <td>{num(trainerSalesTotals.sales)}</td>
                              <td>{num(trainerSalesTotals.clients)}</td>
                              <td colSpan={3}></td>
                              <td>
                                <strong>{money(trainerSalesTotals.net)}</strong>
                              </td>
                            </tr>
                          </>
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-muted">
                              No trainer PT sales for this month yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-2">
                    <Link to="/pt-sales-report" className="btn btn-sm btn-outline-danger">
                      Open PT Sales Report
                    </Link>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-6">
                    <div className="fm-panel">
                      <div className="fm-panel-head">
                        <h5>Present trainers</h5>
                        <span className="fm-chip">{num(presentTrainers.length)} listed</span>
                      </div>
                      <div className="fm-table-wrap">
                        <table className="fm-table">
                          <thead>
                            <tr>
                              {data?.is_all_branches ? <th>Branch</th> : null}
                              <th>Name</th>
                              <th>Dept</th>
                              <th>In</th>
                            </tr>
                          </thead>
                          <tbody>
                            {presentTrainers.length ? (
                              presentTrainers.map((row) => (
                                <tr key={`${row.branch}-${row.id}`}>
                                  {data?.is_all_branches ? <td>{row.branch}</td> : null}
                                  <td>{row.name}</td>
                                  <td>{row.department}</td>
                                  <td>{row.checkin}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={data?.is_all_branches ? 4 : 3} className="text-muted">
                                  No PT trainers marked present for this date
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-xl-6">
                    <div className="fm-panel">
                      <div className="fm-panel-head">
                        <h5>Recent sessions today</h5>
                        <span className="fm-chip">Latest 15</span>
                      </div>
                      <div className="fm-table-wrap">
                        <table className="fm-table">
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Client</th>
                              <th>Trainer</th>
                              <th>Slot</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recent.length ? (
                              recent.map((row) => (
                                <tr key={row.id}>
                                  <td>
                                    <span className={`fm-pill ${typePill(row.type)}`}>{row.type}</span>
                                  </td>
                                  <td>{row.client}</td>
                                  <td>{row.trainer}</td>
                                  <td>{row.time_slot}</td>
                                  <td>
                                    <span
                                      className={`fm-pill ${
                                        String(row.status).toLowerCase() === "delivered" ? "fm-pill-delivered" : ""
                                      }`}
                                    >
                                      {row.status}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="text-muted">
                                  No sessions recorded for this date
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="fm-section-title">Quick access</h3>
                <div className="fm-panel mb-4">
                  <div className="fm-quick-links">
                    {quickLinks.map((link) => (
                      <Link key={link.to} to={link.to}>
                        <i className={`bi ${link.icon}`}></i>
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
